import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { errorLog, infoLog, warnLog } from "../../utils/logger";
import { DocketAPIError, getAPIClient } from "../../apiClient";
import { useNavigate } from "react-router";
import { ImmunizationPatientContact, ImmunizationSearch } from "../../models/Interfaces";
import {
  formatNumber,
  setPinAllowedDate,
  getPinAllowedDate,
  setPinAttempts,
  getPinAttempts,
  getDateTime,
} from "../../utils/data";
import { DateTime } from "luxon";
import { IconButton } from "../../components/IconButton/IconButton";
import { ErrorModal } from "../../components/modals/ErrorModal";
import { izSearchAPIResultAtom, selectedSearchAtom } from "../../jotai/atoms";
import { useAtom } from "jotai";
import { fetchRecordsUntilUpdated } from "../../utils/polling";
import { LegacyDeliverSearchPinRequestDto } from "@docket/consumer-app-client-typescript-axios";
import EnterPinForm from "./EnterPinForm";

interface AltContactsFormProps {
  onIncorrectContacts?: () => void;
  onVerify?: () => void;
}

export function AltContactsForm({ onIncorrectContacts, onVerify }: AltContactsFormProps) {
  const [search, setSearch] = useAtom(selectedSearchAtom);
  const [readyForPin, setReadyForPin] = useState(false);
  const [_izSearchAPIResult, setIzSearchAPIResult] = useAtom(izSearchAPIResultAtom);
  const hasContacts = () => {
    return (
      search &&
      search.patients[0] &&
      search.patients[0].contacts &&
      search.patients[0].contacts.length > 0
    );
  };

  useEffect(() => {
    // Whenever the search changes, reset the readyForPin state
    setReadyForPin(false);
  }, [search]);

  const contacts = hasContacts() ? search!.patients[0].contacts : [];
  const groupedContacts =
    contacts?.reduce((result, contact) => {
      if (!result[contact.type]) {
        result[contact.type] = [];
      }

      result[contact.type].push(contact);
      return result;
    }, {} as { [key: string]: ImmunizationPatientContact[] }) || {};

  const [isSearchRunning, setIsSearchRunning] = useState(false);
  const [selection, setSelection] = useState("");
  const [phoneSelection, setPhoneSelection] = useState<string | undefined>();
  const [showErrorModal, setShowErrorModal] = useState<string | boolean>();
  const navigate = useNavigate();

  // TODO: i18n on these options. We're doing string-comparison
  const phoneOptions = ["Mobile Phone", "Landline"];
  const { t } = useTranslation();

  const next = async () => {
    let param: LegacyDeliverSearchPinRequestDto = {};
    if (selection != null && selection.length > 0) {
      if (selection.includes("@")) {
        param["email"] = selection;
      } else if (!selection.includes("none")) {
        const parsed = phoneSelection?.includes("Mobile") ? "sms" : "voice";
        param[parsed] = selection;
      }
    }

    infoLog(`contacts param ${selection} ${JSON.stringify(param)}`);
    if (param.sms || param.voice) {
      await checkForPinAttempt(param);
    } else {
      await continueFromAlert(param);
    }
  };

  const continueFromAlert = async (param: LegacyDeliverSearchPinRequestDto) => {
    infoLog(`continueFromAlert param ${JSON.stringify(param)}`);
    if (search === null) {
      errorLog(new Error("Search is somehow null!"));
      navigate("../results");
      return;
    }

    if (param.sms || param.voice || param.email) {
      setIsSearchRunning(true);
      try {
        await getAPIClient().sendImmunizationSearchPIN(search.uid, param, search.izProviderKey);
        /**
         * Do not await this since it could potentially take a long time to
         * complete and we want to navigate immediately.
         */
        fetchRecordsUntilUpdated();
        setReadyForPin(true);
      } catch (e: unknown) {
        if (e instanceof DocketAPIError) {
          setShowErrorModal(e.message);
        }
      }
    } else {
      /**
       * Do not await this since it could potentially take a long time to
       * complete and we want to navigate immediately.
       */
      fetchRecordsUntilUpdated();
      onIncorrectContacts?.();
    }
  };

  const checkForPinAttempt = async (param: LegacyDeliverSearchPinRequestDto) => {
    const attempts = await getPinAttempts();
    const format = "YYYY-MM-DD HH:mm:ss";
    if (attempts >= 3) {
      const dateAllowed = await getPinAllowedDate();
      let compare = null;
      if (dateAllowed && getDateTime(dateAllowed.toString()).isValid) {
        compare = getDateTime(dateAllowed.toString()) < DateTime.now();
      }
      if (dateAllowed && compare) {
        //reset and allow pin call
        await setPinAttempts(true);
        await setPinAllowedDate("");
        await setPinAttempts(false);
        continueFromAlert(param);
      } else if (!dateAllowed) {
        await setPinAllowedDate(DateTime.now().plus({ minutes: 15 }).toFormat(format));
        pinAttemptAlert();
      } else {
        pinAttemptAlert();
      }
    } else {
      //increment pin attempts
      await setPinAttempts(false);
      continueFromAlert(param);
    }
  };

  const pinAttemptAlert = () => {
    setShowErrorModal(`${t("immunizations.alt_contacts_pin_limit")}`);
  };

  const setContactNumberSelection = (number: string, selection: string) => {
    setSelection(number);
    setPhoneSelection(selection);
  };

  const contactCheckboxHandler = (value: any, index: number, phoneValue?: string) => {
    infoLog(`value ${value}, index ${index}`);
    if (index === 999) {
      setContactNumberSelection("-1", "-1");
      setSelection("none");
    } else {
      setContactNumberSelection(value, `${phoneValue ? phoneValue : phoneOptions[0]}${value}`);
    }
  };

  const getContactsCheckboxSection = () => {
    return (
      <div className="flex flex-col">
        {Object.keys(groupedContacts).map((type) => (
          <>
            {groupedContacts[type].map((contact, i) => (
              <div className="field" key={`${contact.maskedValue}-${i}`}>
                <div className="flex gap-2 flex-col">
                  <label
                    className="flex items-center gap-2"
                    data-testid={
                      contact?.maskedValue?.includes("@") ? "email-contact" : "phone-contact"
                    }
                  >
                    <input
                      type="radio"
                      className="radio radio-primary"
                      checked={selection === contact.maskedValue}
                      name={"contactOption"}
                      onChange={() => contactCheckboxHandler(contact.maskedValue, i, undefined)}
                    />
                    {contact?.maskedValue?.includes("@")
                      ? contact.maskedValue
                      : formatNumber(contact?.maskedValue!)}
                  </label>
                  {!contact?.maskedValue?.includes("@") && (
                    <div className="flex gap-2 ml-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          className="radio radio-primary"
                          checked={
                            selection === contact.maskedValue &&
                            phoneSelection === `${phoneOptions[0]}${contact?.maskedValue!}`
                          }
                          name={"contactSubOption"}
                          onChange={() =>
                            contactCheckboxHandler(contact.maskedValue, i, phoneOptions[0])
                          }
                        />
                        {phoneOptions[0]}
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          className="radio radio-primary"
                          checked={
                            selection === contact.maskedValue &&
                            phoneSelection === `${phoneOptions[1]}${contact?.maskedValue!}`
                          }
                          name={"contactSubOption"}
                          onChange={() =>
                            contactCheckboxHandler(contact.maskedValue, i, phoneOptions[1])
                          }
                        />
                        {phoneOptions[1]}
                      </label>
                    </div>
                  )}
                </div>
                <br />
              </div>
            ))}
          </>
        ))}
        <div className="field" key={`none`}>
          <label className="">
            <input
              type="radio"
              className="radio radio-primary"
              checked={selection === "none"}
              name={"contactOption"}
              onChange={() =>
                contactCheckboxHandler(t("immunizations.alt_contacts_recognize")!, 999)
              }
            />
            &nbsp;&nbsp;{t("immunizations.alt_contacts_recognize")!}
          </label>
          <br />
        </div>
      </div>
    );
  };

  const getMessage1Text = () => {
    return t("immunizations.alt_contacts_header2").split("|");
  };

  const getInfoText = () => {
    return t("immunizations.alt_contacts_pin_limit").split("|");
  };

  return (
    <>
      {showErrorModal && (
        <ErrorModal onCloseModal={() => setShowErrorModal(false)}>{showErrorModal}</ErrorModal>
      )}
      {readyForPin && <EnterPinForm onVerify={onVerify} />}
      {!readyForPin && (
        <div className="flex flex-col gap-4 h-full">
          <div className="flex-1 overflow-y-auto flex flex-col gap-2">
            <p>{getMessage1Text().join(" ")}</p>
            {getContactsCheckboxSection()}
            <p className="text-sm">{getInfoText()[0]}</p>
          </div>
          <div className="shrink-0 max-md:pb-8 ">
            <button
              type="submit"
              className="btn btn-lg btn-primary w-full"
              onClick={next}
              disabled={
                !selection ||
                (!selection.includes("@") &&
                  phoneSelection !== "-1" &&
                  !phoneSelection?.includes("Mobile") &&
                  !phoneSelection?.includes("Landline"))
              }
            >
              {t("generic.next")}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default AltContactsForm;
